import { Link, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import Navbar from "../components/Navbar";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";
import {
  FaHeart,
  FaStar,
  FaShoppingCart,
  FaTruck,
  FaShieldAlt,
  FaUndo,
  FaBolt,
  FaPlus,
  FaMinus,
  FaCheck,
} from "react-icons/fa";
import { FiArrowLeft, FiChevronRight, FiShare2 } from "react-icons/fi";
import toast from "react-hot-toast";
import BASE_URL, { getImageUrl } from "../BASEURL";

const PLACEHOLDER = "https://via.placeholder.com/800x800?text=No+Image";

const toastDark = {
  position: "top-center",
  style: { background: "#1c1917", color: "#fff", borderRadius: "12px" },
};

const getProductImages = (p) => {
  if (!p) return [];
  if (Array.isArray(p.images) && p.images.length > 0) return p.images;
  if (p.image) return [p.image];
  return [];
};

const truncateDescription = (html, isExpanded) => {
  if (!html) return "";
  if (isExpanded) return html;

  const text = html.replace(/<[^>]*>/g, "");
  if (text.length <= 500) return html;

  let result = "";
  let charCount = 0;
  let inTag = false;
  let tagBuffer = "";

  for (let i = 0; i < html.length; i++) {
    const char = html[i];
    if (char === "<") {
      inTag = true;
      tagBuffer = char;
    } else if (char === ">" && inTag) {
      inTag = false;
      tagBuffer += char;
      result += tagBuffer;
      tagBuffer = "";
    } else if (inTag) {
      tagBuffer += char;
    } else if (charCount < 500) {
      result += char;
      charCount++;
    } else {
      break;
    }
  }
  return `${result}...`;
};

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [cartQty, setCartQty] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [expandedDescription, setExpandedDescription] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);

  const backendUrl = BASE_URL;

  const imageList = useMemo(() => getProductImages(product), [product]);

  const mainImage = useMemo(() => {
    const src = imageList[activeImageIndex];
    return src ? getImageUrl(src) : PLACEHOLDER;
  }, [imageList, activeImageIndex]);

  const displayPrice = useMemo(() => {
    if (!product) return { current: 0, original: null, savePercent: 0 };
    const current =
      selectedSize?.discountPrice ??
      selectedSize?.price ??
      product.discountPrice ??
      product.price ??
      0;
    const original = selectedSize?.price ?? product.price ?? null;
    const hasDiscount =
      original != null && Number(current) < Number(original);
    const savePercent = hasDiscount
      ? Math.round(((Number(original) - Number(current)) / Number(original)) * 100)
      : 0;
    return { current, original: hasDiscount ? original : null, savePercent };
  }, [product, selectedSize]);

  const productSchema = product
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.productName || product.name || "Holly Zolly Product",
        image: imageList.map((img) => getImageUrl(img)),
        description: (product.description || product.shortDescription || product.productName || "").replace(/<[^>]*>/g, ""),
        sku: product.sku || product._id || "",
        brand: {
          "@type": "Brand",
          name: "Holly Zolly",
        },
        offers: {
          "@type": "Offer",
          url: `${window.location.origin}/product/${product._id}`,
          priceCurrency: "INR",
          price: displayPrice.current,
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/NewCondition",
        },
      }
    : null;

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setExpandedDescription(false);
        setActiveImageIndex(0);

        const res = await fetch(`${backendUrl}/api/product/${id}`);
        const data = await res.json();
        const found =
          data.product || data.data?.product || data.data || data || null;

        if (!found || !found._id) {
          setProduct(null);
          return;
        }

        setProduct(found);

        const wishlist = JSON.parse(localStorage.getItem("wishlistItems")) || [];
        setIsWishlisted(wishlist.some((item) => item._id === found._id));

        if (found.sizes?.length > 0) {
          setSelectedSize(found.sizes[0]);
        } else {
          setSelectedSize(null);
        }
      } catch (err) {
        console.error(err);
        setProduct(null);
        toast.error("Could not load product");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, backendUrl]);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/product/all`);
        const data = await res.json();
        if (Array.isArray(data)) setProducts(data);
        else if (Array.isArray(data.products)) setProducts(data.products);
        else if (Array.isArray(data.data)) setProducts(data.data);
        else if (Array.isArray(data.data?.products)) setProducts(data.data.products);
        else setProducts([]);
      } catch (err) {
        console.error(err);
      }
    };
    fetchAllProducts();
  }, [backendUrl]);

  const getCurrentUser = () => {
    try {
      return JSON.parse(localStorage.getItem("currentUser"));
    } catch {
      return null;
    }
  };

  const getCartKey = (user) => {
    const email = user?.email || user?.emailId;
    return email ? `cartItems_${email}` : "cartItems_guest";
  };

  const findCartItemIndex = useCallback(
    (cart) =>
      cart.findIndex(
        (item) =>
          String(item._id) === String(product?._id) &&
          item.selectedSize === (selectedSize?.size || "")
      ),
    [product?._id, selectedSize?.size]
  );

  useEffect(() => {
    const syncCartQty = () => {
      if (!product) {
        setCartQty(0);
        return;
      }
      const user = getCurrentUser();
      const cart = JSON.parse(localStorage.getItem(getCartKey(user))) || [];
      const idx = findCartItemIndex(cart);
      setCartQty(idx > -1 ? cart[idx].quantity : 0);
    };
    syncCartQty();
    window.addEventListener("cartUpdated", syncCartQty);
    return () => window.removeEventListener("cartUpdated", syncCartQty);
  }, [product, selectedSize, findCartItemIndex]);

  const buildCartItem = (qty) => ({
    _id: product._id,
    name: product.productName || product.name || "",
    price:
      selectedSize?.discountPrice ||
      selectedSize?.price ||
      product.discountPrice ||
      product.price ||
      0,
    discountPrice:
      selectedSize?.discountPrice || product.discountPrice || product.price,
    selectedSize: selectedSize?.size || "",
    image: product.images?.[0] || product.image,
    quantity: qty,
  });

  const updateCartQuantity = (newQty) => {
    const user = getCurrentUser();
    const cartKey = getCartKey(user);
    let cart = JSON.parse(localStorage.getItem(cartKey)) || [];
    const idx = findCartItemIndex(cart);

    if (newQty <= 0) {
      if (idx > -1) cart.splice(idx, 1);
      setCartQty(0);
    } else if (idx > -1) {
      cart[idx].quantity = newQty;
      setCartQty(newQty);
    } else {
      cart.push(buildCartItem(newQty));
      setCartQty(newQty);
    }

    localStorage.setItem(cartKey, JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const addToCart = () => {
    if (product.sizes?.length > 0 && !selectedSize) {
      toast.error("Please select a size");
      return;
    }
    updateCartQuantity(cartQty > 0 ? cartQty + 1 : 1);
    toast.success("Added to cart", {
      position: "bottom-right",
      style: { background: "#1c1917", color: "#fff", borderRadius: "12px" },
    });
  };

  const buyNow = () => {
    if (product.sizes?.length > 0 && !selectedSize) {
      toast.error("Please select a size");
      return;
    }

    const user = getCurrentUser();
    const cartKey = getCartKey(user);
    let cart = JSON.parse(localStorage.getItem(cartKey)) || [];
    const idx = findCartItemIndex(cart);
    const qty = cartQty > 0 ? cartQty : 1;

    if (idx > -1) cart[idx].quantity = qty;
    else cart.push(buildCartItem(qty));

    setCartQty(qty);
    localStorage.setItem(cartKey, JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    navigate("/cart");
  };

  const toggleWishlist = () => {
    const wishlist = JSON.parse(localStorage.getItem("wishlistItems")) || [];
    const itemIndex = wishlist.findIndex((item) => item._id === product._id);

    if (itemIndex > -1) {
      wishlist.splice(itemIndex, 1);
      setIsWishlisted(false);
      toast.success("Removed from wishlist");
    } else {
      wishlist.push({
        _id: product._id,
        name: product.productName || product.name || "",
        price: product.discountPrice || product.price || 0,
        image: product.images?.[0] || product.image || "",
      });
      setIsWishlisted(true);
      toast.success("Added to wishlist");
    }

    localStorage.setItem("wishlistItems", JSON.stringify(wishlist));
    window.dispatchEvent(new Event("wishlistUpdated"));
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.productName,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch {
      /* user cancelled */
    }
  };

  const selectImage = (index) => {
    setActiveImageIndex(index);
  };

  const selectSize = (sizeObj) => {
    setSelectedSize(sizeObj);
  };

  const relatedProducts = useMemo(
    () =>
      products.filter(
        (p) =>
          p._id !== product?._id &&
          p.categoryId?._id === product?.categoryId?._id
      ),
    [products, product]
  );

  const descriptionPlainLength =
    product?.description?.replace(/<[^>]*>/g, "").length || 0;

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
            <div className="h-4 w-40 bg-stone-200 rounded animate-pulse mb-8" />
            <div className="grid lg:grid-cols-2 gap-10">
              <div className="aspect-square rounded-3xl bg-stone-200/70 animate-pulse" />
              <div className="space-y-4">
                <div className="h-6 w-28 bg-stone-200 rounded-full animate-pulse" />
                <div className="h-12 w-full bg-stone-200 rounded-xl animate-pulse" />
                <div className="h-10 w-48 bg-stone-200 rounded-lg animate-pulse mt-6" />
                <div className="h-32 w-full bg-stone-200 rounded-2xl animate-pulse mt-8" />
                <div className="h-14 w-full bg-stone-200 rounded-2xl animate-pulse mt-8" />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center bg-gradient-to-b from-stone-50 to-white px-4">
          <p className="text-2xl font-semibold text-stone-800">Product not found</p>
          <p className="text-stone-500 mt-2 mb-8">This item may have been removed.</p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-orange-600 transition-colors"
          >
            <FiArrowLeft />
            Back to Shop
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={product.productName ? `${product.productName} | Holly Zolly` : "Holly Zolly Product"}
        description={
          (product.description || product.shortDescription || product.productName || "")
            .replace(/<[^>]*>/g, "")
            .slice(0, 160)
        }
        image={getImageUrl(product.images?.[0] || product.image) || "https://hollyzolly.shop/image/logo/LOGO.png"}
        type="product"
        schema={productSchema}
      />
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-stone-50 via-white to-orange-50/20">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-stone-500">
            <Link to="/" className="hover:text-orange-600 transition-colors">
              Home
            </Link>
            <FiChevronRight className="shrink-0" size={14} />
            <Link to="/shop" className="hover:text-orange-600 transition-colors">
              Shop
            </Link>
            {product.categoryId?.name && (
              <>
                <FiChevronRight className="shrink-0" size={14} />
                <Link
                  to={`/shop?category=${encodeURIComponent(product.categoryId.name)}`}
                  className="hover:text-orange-600 transition-colors"
                >
                  {product.categoryId.name}
                </Link>
              </>
            )}
            <FiChevronRight className="shrink-0" size={14} />
            <span className="text-stone-800 font-medium truncate max-w-[180px] sm:max-w-xs">
              {product.productName}
            </span>
          </nav>

          <Link
            to="/shop"
            className="inline-flex items-center gap-2 mt-4 text-stone-600 hover:text-orange-600 font-medium text-sm transition-colors"
          >
            <FiArrowLeft size={16} />
            Continue shopping
          </Link>
        </div>

        {/* Main product */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Gallery */}
            <div className="lg:sticky lg:top-24 space-y-4">
              <div className="relative group rounded-3xl overflow-hidden bg-white border border-stone-200/80 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.12)]">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50/40 via-transparent to-stone-50/50 pointer-events-none z-0" />
                <div className="aspect-square p-6 sm:p-10 relative z-10">
                  <img
                    key={mainImage}
                    src={mainImage}
                    alt={product.productName}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                    onError={(e) => {
                      e.target.src = PLACEHOLDER;
                    }}
                  />
                </div>
                {displayPrice.savePercent > 0 && (
                  <span className="absolute top-5 left-5 z-20 bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                    {displayPrice.savePercent}% OFF
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleShare}
                  className="absolute top-5 right-5 z-20 w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-stone-200 text-stone-600 hover:text-orange-600 hover:border-orange-200 flex items-center justify-center shadow-md transition-all"
                  aria-label="Share product"
                >
                  <FiShare2 size={18} />
                </button>
              </div>

              {imageList.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-thin">
                  {imageList.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => selectImage(idx)}
                      className={`relative w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-2xl overflow-hidden border-2 transition-all duration-200 ${
                        activeImageIndex === idx
                          ? "border-orange-500 ring-2 ring-orange-200 scale-[1.02]"
                          : "border-stone-200 hover:border-orange-300 opacity-80 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${product.productName} image ${idx + 1}`}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = PLACEHOLDER;
                        }}
                      />
                      {activeImageIndex === idx && (
                        <span className="absolute inset-0 ring-inset ring-1 ring-white/50 rounded-2xl pointer-events-none" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="lg:pt-2">
              {product.categoryId?.name && (
                <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-orange-700 bg-orange-50 border border-orange-100 px-3 py-1 rounded-full">
                  {product.categoryId.name}
                </span>
              )}

              <h1 className="mt-4 text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-stone-900 leading-[1.15] tracking-tight">
                {product.productName}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-5">
                <div className="inline-flex items-center gap-1.5 bg-emerald-600 text-white pl-2.5 pr-3 py-1.5 rounded-lg text-sm font-bold shadow-sm">
                  <FaStar className="text-amber-300 text-xs" />
                  {product.rating || "4.8"}
                </div>
                <span className="text-stone-500 text-sm">Trusted by 1,200+ customers</span>
                <span className="hidden sm:inline text-stone-300">|</span>
                <span className="text-emerald-700 text-sm font-medium flex items-center gap-1">
                  <FaCheck className="text-xs" /> In stock
                </span>
              </div>

              {/* Price */}
              <div className="mt-8 p-5 sm:p-6 rounded-2xl bg-white border border-stone-200/80 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
                  Price
                  {selectedSize?.size ? ` · Size ${selectedSize.size}` : ""}
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <span className="text-4xl sm:text-5xl font-bold text-stone-900 tracking-tight">
                    ₹{displayPrice.current}
                  </span>
                  {displayPrice.original != null && (
                    <>
                      <span className="text-xl text-stone-400 line-through mb-1">
                        ₹{displayPrice.original}
                      </span>
                      <span className="mb-1 text-sm font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                        Save {displayPrice.savePercent}%
                      </span>
                    </>
                  )}
                </div>
                <p className="text-stone-500 text-sm mt-2">Inclusive of all taxes</p>
              </div>

              {/* Sizes */}
              {product.sizes?.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-stone-800 mb-3">
                    Select size
                  </h3>
                  <div className="flex flex-wrap gap-2.5">
                    {product.sizes.map((sizeObj, idx) => {
                      const isActive = selectedSize?.size === sizeObj.size;
                      const sizePrice =
                        sizeObj.discountPrice ?? sizeObj.price ?? displayPrice.current;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectSize(sizeObj)}
                          className={`min-w-[3.5rem] px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all duration-200 ${
                            isActive
                              ? "border-stone-900 bg-stone-900 text-white shadow-md"
                              : "border-stone-200 bg-white text-stone-700 hover:border-orange-400 hover:bg-orange-50/50"
                          }`}
                        >
                          <span className="block">{sizeObj.size}</span>
                          {(sizeObj.discountPrice || sizeObj.price) && (
                            <span
                              className={`block text-[10px] mt-0.5 font-medium ${
                                isActive ? "text-stone-300" : "text-stone-400"
                              }`}
                            >
                              ₹{sizePrice}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                {cartQty > 0 ? (
                  <div className="flex-1 flex items-center justify-center gap-4 bg-stone-900 text-white py-4 px-6 rounded-2xl font-bold shadow-lg">
                    <button
                      type="button"
                      onClick={() => updateCartQuantity(cartQty - 1)}
                      className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
                      aria-label="Decrease quantity"
                    >
                      <FaMinus size={12} />
                    </button>
                    <span className="text-lg min-w-[2rem] text-center tabular-nums">
                      {cartQty}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateCartQuantity(cartQty + 1)}
                      className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
                      aria-label="Increase quantity"
                    >
                      <FaPlus size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={addToCart}
                    className="flex-1 flex items-center justify-center gap-2.5 bg-stone-900 hover:bg-orange-600 text-white py-4 px-6 rounded-2xl font-bold transition-all duration-300 shadow-lg hover:shadow-orange-200/50"
                  >
                    <FaShoppingCart size={18} />
                    Add to cart
                  </button>
                )}

                <button
                  type="button"
                  onClick={buyNow}
                  className="flex-1 flex items-center justify-center gap-2.5 bg-orange-600 hover:bg-orange-700 text-white py-4 px-6 rounded-2xl font-bold transition-all duration-300 shadow-lg shadow-orange-200/60"
                >
                  <FaBolt size={16} />
                  Buy now
                </button>

                <button
                  type="button"
                  onClick={toggleWishlist}
                  aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                  className={`sm:w-14 shrink-0 h-14 rounded-2xl border-2 flex items-center justify-center transition-all ${
                    isWishlisted
                      ? "bg-red-50 border-red-200 text-red-500"
                      : "border-stone-200 bg-white text-stone-400 hover:border-red-200 hover:text-red-500"
                  }`}
                >
                  <FaHeart size={20} className={isWishlisted ? "fill-current" : ""} />
                </button>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-3 mt-8">
                {[
                  { icon: FaTruck, label: "Free delivery" },
                  { icon: FaShieldAlt, label: "Secure payment" },
                  { icon: FaUndo, label: "Easy returns" },
                ].map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex flex-col items-center text-center p-4 rounded-2xl bg-white border border-stone-100 shadow-sm"
                  >
                    <div className="w-11 h-11 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center mb-2">
                      <Icon size={18} />
                    </div>
                    <p className="text-[11px] sm:text-xs font-semibold text-stone-700 leading-tight">
                      {label}
                    </p>
                  </div>
                ))}
              </div>

              {/* Description */}
              {product.description && (
                <div className="mt-10 rounded-2xl bg-white border border-stone-200/80 p-6 sm:p-8 shadow-sm">
                  <h2 className="text-lg font-bold text-stone-900 mb-4">Product details</h2>
                  <div
                    className="prose prose-stone max-w-none text-stone-600 text-sm sm:text-base leading-relaxed break-words overflow-hidden [&_p]:whitespace-normal [&_span]:whitespace-normal [&_p]:break-words [&_span]:break-words [&_strong]:font-bold [&_strong]:text-stone-800 [&_b]:font-bold [&_p]:mb-3 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_li]:mb-1 [&_a]:text-orange-600 [&_a]:underline"
                    dangerouslySetInnerHTML={{
                      __html: truncateDescription(
                        product.description,
                        expandedDescription
                      ),
                    }}
                  />
                  {descriptionPlainLength > 500 && (
                    <button
                      type="button"
                      onClick={() => setExpandedDescription(!expandedDescription)}
                      className="mt-4 text-orange-600 font-semibold text-sm hover:text-orange-700 transition-colors"
                    >
                      {expandedDescription ? "Show less" : "Read more"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Related */}
        {relatedProducts.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-4">
            <div className="flex items-end justify-between gap-4 mb-8 border-t border-stone-200/80 pt-12">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-2">
                  You may also like
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">
                  Related products
                </h2>
              </div>
              <Link
                to="/shop"
                className="text-sm font-semibold text-orange-600 hover:text-orange-700 whitespace-nowrap"
              >
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {relatedProducts.slice(0, 4).map((item) => (
                <ProductCard key={item._id} product={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
